import {
  ClientOrganizationRoleEnum,
  ClientStatisticsEmailConfigUpdateRequestFrequencyEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Divider, RadioButton, Text } from 'react-native-paper';

import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useGetStatisticsEmailConfig } from '@/features/api/hooks/grapevine-organization/useGetStatisticsEmailConfig';
import { useUpdateStatisticsEmailConfig } from '@/features/api/hooks/grapevine-organization/useUpdateStatisticsEmailConfig';
import type { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { GradientBackground } from '@/shared/components/GradientBackground';
import {
  OrganizationAccessProvider,
  useOrganizationAccess
} from '@/shared/components/organization';
import { OrganizationAccessState } from '@/shared/components/organization/organizationAccess';
import { StateView } from '@/shared/components/state-view/StateView';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

type Props = AppNativeStackScreenProps<
  typeof ProfileSettingRoute.StatisticsEmail
>;

type Frequency = ClientStatisticsEmailConfigUpdateRequestFrequencyEnum;
type SelectionValue = 'OFF' | Frequency;

const MANAGER_ROLES = [ClientOrganizationRoleEnum.Admin];

// The backend defaults for a caller that has never saved a config. Also used
// as the initial `lastFrequency` before the GET returns.
const DEFAULT_FREQUENCY: Frequency =
  ClientStatisticsEmailConfigUpdateRequestFrequencyEnum.Monthly;
const DEFAULT_ENABLED = true;

const StatisticsEmailContent: FC<Props> = ({ route, navigation }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { organizationId } = route.params;

  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.statistics-email'
  });

  const { data: organization } = useGetOrganizationById(organizationId);

  const {
    data: config,
    isLoading,
    isRefetching,
    refetch,
    isError
  } = useGetStatisticsEmailConfig(organizationId);

  const { mutate: updateConfig, isPending: isUpdating } =
    useUpdateStatisticsEmailConfig();

  // Preserve the last non-OFF choice so re-enabling can restore it in the UI
  // without waiting for the round-trip. The backend also preserves it, so the
  // two sources agree on the next GET.
  const [lastFrequency, setLastFrequency] =
    useState<Frequency>(DEFAULT_FREQUENCY);

  useEffect(() => {
    if (config?.frequency) {
      setLastFrequency(config.frequency);
    }
  }, [config?.frequency]);

  const currentSelection: SelectionValue = useMemo(() => {
    if (!config) return DEFAULT_FREQUENCY;
    const enabled = config.enabled ?? DEFAULT_ENABLED;
    if (!enabled) return 'OFF';
    return config.frequency ?? DEFAULT_FREQUENCY;
  }, [config]);

  const managerAccess = useOrganizationAccess({ roles: MANAGER_ROLES });
  const isDenied = managerAccess === OrganizationAccessState.Denied;

  const handleSelect = useCallback(
    (value: SelectionValue) => {
      if (value === currentSelection) return;
      if (value === 'OFF') {
        updateConfig({
          organizationId,
          clientStatisticsEmailConfigUpdateRequest: {
            enabled: false,
            frequency: lastFrequency
          }
        });
      } else {
        setLastFrequency(value);
        updateConfig({
          organizationId,
          clientStatisticsEmailConfigUpdateRequest: {
            enabled: true,
            frequency: value
          }
        });
      }
    },
    [currentSelection, updateConfig, organizationId, lastFrequency]
  );

  useEffect(() => {
    navigation.setOptions({
      header: () => (
        <NavigationBar title={t('title')} subTitle={organization?.name} />
      )
    });
  }, [navigation, organization?.name, t]);

  return (
    <GradientBackground>
      <StateView
        isLoading={isLoading}
        isRefetching={isRefetching}
        isError={isError && !isDenied}
        isEmpty={!isLoading && isDenied}
        onRefetch={refetch}
        stateLabels={{
          loading: t('state.loading', { returnObjects: true }),
          error: t('state.error', { returnObjects: true }),
          empty: t('state.noAccess', { returnObjects: true })
        }}
        backgroundColor="transparent"
        contentContainerStyle={styles.content}
      >
        <View style={styles.header}>
          <Text variant="headlineSmall">{t('heading')}</Text>
          <Text variant="bodyMedium" style={styles.description}>
            <Trans
              i18nKey="profile-setting:screens.statistics-email.description"
              components={{ bold: <Text style={styles.bold}>{''}</Text> }}
            />
          </Text>
        </View>

        <View
          style={styles.radioSection}
          pointerEvents={isUpdating ? 'none' : 'auto'}
        >
          <RadioButton.Group
            value={currentSelection}
            onValueChange={(v) => handleSelect(v as SelectionValue)}
          >
            <RadioButton.Item
              label={t('options.off')}
              value="OFF"
              labelVariant="bodyLarge"
              position="leading"
              labelStyle={styles.radioLabel}
              style={styles.radioItem}
            />
            <Divider />
            <RadioButton.Item
              label={t('options.weekly')}
              value={
                ClientStatisticsEmailConfigUpdateRequestFrequencyEnum.Weekly
              }
              labelVariant="bodyLarge"
              position="leading"
              labelStyle={styles.radioLabel}
              style={styles.radioItem}
            />
            <Divider />
            <RadioButton.Item
              label={t('options.monthly')}
              value={
                ClientStatisticsEmailConfigUpdateRequestFrequencyEnum.Monthly
              }
              labelVariant="bodyLarge"
              position="leading"
              labelStyle={styles.radioLabel}
              style={styles.radioItem}
            />
          </RadioButton.Group>
        </View>
      </StateView>
    </GradientBackground>
  );
};

export const StatisticsEmailScreen: FC<Props> = (props) => {
  const {
    route: {
      params: { organizationId }
    }
  } = props;
  const { data: organization } = useGetOrganizationById(organizationId);

  return (
    <OrganizationAccessProvider organization={organization}>
      <StatisticsEmailContent {...props} />
    </OrganizationAccessProvider>
  );
};

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    content: { paddingBottom: 32, gap: 16 },
    header: {
      paddingHorizontal: 16,
      paddingTop: 24,
      paddingBottom: 8,
      gap: 8
    },
    description: { color: theme.colors.onSurfaceVariant },
    bold: { fontWeight: '700' },
    radioSection: { backgroundColor: theme.colors.surfaceContainerLowest },
    radioItem: { paddingVertical: 4, paddingHorizontal: 16 },
    radioLabel: { textAlign: 'left' }
  });
